<template>
  <div>
    <h1>REDCap Submitters</h1>

    <b-button v-b-modal.modal-crud variant="primary" style="margin-bottom:20px;">New REDCap Submitter</b-button>
    
    <b-modal id="modal-crud" hide-header hide-footer>
      <h2>{{formTitle}}</h2>
      <hr/>
      <form ref="form" v-on:submit.prevent="save">

          <b-form-group 
            label="Visit and Closure Data Access Group" label-for="dag-input"
              invalid-feedback="Visit and Closure Data Access Group is required.">
            <b-form-input id="dag-input" 
              v-model="$v.editedItem.dataAccessGroupId.$model" 
              :state="$v.editedItem.dataAccessGroupId.$dirty ? !$v.editedItem.dataAccessGroupId.$error : null"
              aria-describedby="input-dag-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-dag-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>             
            <b-form-text id="dag-input-help">Required.</b-form-text>     
          </b-form-group>

          <b-form-group
            label="Home Visitor - Data Access Group" label-for="hv-dag-input"
              invalid-feedback="Home Visitor - Data Access Group is required.">
            <b-form-input id="hv-dag-input"
              v-model="$v.editedItem.homeVisitorDataAccessGroupId.$model"
              :state="$v.editedItem.homeVisitorDataAccessGroupId.$dirty ? !$v.editedItem.homeVisitorDataAccessGroupId.$error : null"
              aria-describedby="input-hv-dag-live-feedback"
              required>
            </b-form-input>
            <b-form-invalid-feedback id="input-hv-dag-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>
            <b-form-text id="hv-dag-input-help">Required.</b-form-text>
          </b-form-group>

        <b-form-group 
            label="Submitter Name" label-for="name-input" 
              invalid-name-feedback="Submitter Name is required.">
            <b-form-input id="name-input" 
              v-model="$v.editedItem.name.$model" 
              :state="$v.editedItem.name.$dirty ? !$v.editedItem.name.$error : null"
              aria-describedby="input-name-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-name-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>     
            <b-form-text id="name-input-help">Required.</b-form-text>     
          </b-form-group>

          <b-form-group 
            label="Submitter Email Address" label-for="email-input" 
              invalid-name-feedback="Submitter Email Address is required.">
            <b-form-input id="email-input" 
              v-model="$v.editedItem.emailAddress.$model" 
              :state="$v.editedItem.emailAddress.$dirty ? !$v.editedItem.emailAddress.$error : null"
              aria-describedby="input-email-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-email-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>     
            <b-form-text id="email-input-help">Required.</b-form-text>     
          </b-form-group>

          <b-form-group 
            label="Submitter Email Address - Secondary" label-for="email-input" 
              invalid-name-feedback="Secondary Submitter Email Address must be less than 254 characters.">
            <b-form-input id="email-input" 
              v-model="$v.editedItem.secondaryEmailAddress.$model" 
              :state="$v.editedItem.secondaryEmailAddress.$dirty ? !$v.editedItem.secondaryEmailAddress.$error : null"
              aria-describedby="input-email2-live-feedback">
            </b-form-input> 
            <b-form-invalid-feedback id="input-email2-live-feedback">
              Secondary Submitter Email Address must be less than 254 characters.
            </b-form-invalid-feedback>     
          </b-form-group>

          <b-form-group label="Provider" label-for="provider-input">
            <b-form-select id="provider-input"
                v-model="$v.editedItem.providerId.$model"  
                :state="$v.editedItem.providerId.$dirty ? !$v.editedItem.providerId.$error : null"
                  aria-describedby="input-provider-live-feedback"              
                :options="providers"
                value-field="mdhProviderId"
                text-field="fhvProviderName"
                aria-label="Select Provider">
                <template slot="first">
                    <option :value="null" disabled>Select Provider</option>
                </template>
            </b-form-select>
            <b-form-invalid-feedback id="input-provider-live-feedback">
              This is a required field.
            </b-form-invalid-feedback> 
            <b-form-text id="provider-input-help">Required.</b-form-text>     
          </b-form-group>

          <b-button @click="close" style="margin-right:15px;">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="$v.editedItem.$invalid">Save</b-button>
        </form>
    </b-modal>

    <div>
      <b-table hover small responsive
        :fields="fields"
        :items="submitters"
        :busy="isBusy"
        style="font-size: .85em;">
        <template v-slot:cell(actions)="row">
          <b-button size="sm" @click="editItem(row.item)" class="mr-1">
            edit
          </b-button>
          <b-button size="sm" @click="deleteItem(row.item)" class="mr-1">
            delete
          </b-button>
        </template>

        <template v-slot:cell(fullName)="data">
          <span>{{ data.item.name }}<br/> {{ data.item.providerName }}</span>
        </template>

        <template v-slot:cell(healthStatus)="row">
           <b-button size="sm" @click="viewErrors(row.item)" class="mr-1" v-if="row.value === 'Error'">
            View Errors
          </b-button>
          <span v-else>{{row.value}}</span>
        </template>

        <template v-slot:cell(homeVisitorHealthStatus)="row">
           <b-button size="sm" @click="viewVisitorErrors(row.item)" class="mr-1"
             v-if="row.value === 'Error'">
            View Errors
          </b-button>
          <span v-else>{{row.value}}</span>
        </template>

        <template v-slot:cell(closureStatus)="row">
          <b-button size="sm" @click="viewClosureErrors(row.item)" class="mr-1"
                    v-if="row.value === 'Error'">
            View Errors
          </b-button>
          <span v-else>{{row.value}}</span>
        </template>

        <div slot="table-busy" class="text-center text-danger my-2">
          <div
          small variant="primary"
          role="alert"
          aria-busy = true;
          aria-live="assertive"
          aria-atomic="true"
          aria-label = "Data is loading.  Please wait for the load to finish."
          label="Searching, please wait for the specific data load says the label"
          alt="Processing... please wait for the specific data load says the alt"
          title= "Data Loading is our Title">
          </div>
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>          
      </b-table>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import axiosRetry from 'axios-retry';
  import { validationMixin } from 'vuelidate'
  import { required, minLength, maxLength } from 'vuelidate/lib/validators'

  axiosRetry(axios, { retries: 3 });

  export default {
    mixins: [validationMixin],
    title:  'REDCap Submitters',
    data: () => ({
      isBusy: false,
      fields: [
        {key: 'dataAccessGroupId', sortable: true, label: 'Visit/Closure DAG'},
        {key: 'homeVisitorDataAccessGroupId', sortable: true, label: 'Visitor DAG'},
        /*
        {key: 'name', sortable: true, label: 'Submitter Name'},
        {key: 'providerName', sortable: true, label: 'Provider Name'},
        */
        {key: 'fullName', label: 'Submitter/Provider' },
        {key: 'healthStatus', sortable: true, label: 'Visit Status'},        
        {key: 'errorCount', sortable: true, label: 'Visit Errors'},
        {key: 'lastSubmissionDate', sortable: true, label: 'Visit Last Submission'},
        
        {key: 'homeVisitorHealthStatus', sortable: true, label: 'Visitor Status'},
        {key: 'homeVisitorErrorCount', sortable: true, label: 'Visitor Errors'},
        {key: 'homeVisitorLastSubmissionDate', sortable: true, label: 'Visitor Last Submission'},

        {key: 'closureStatus', sortable: true, label: 'Closure Status'},
        {key: 'closureErrorCount', sortable: true, label: 'Closure Errors'},
        {key: 'closureLastSubmissionDate', sortable: true, label: 'Closure Last Submission'},
        {key: 'actions', label: 'Actions'}
      ],
      submitters: [],
      providers: [],
      editedIndex: -1,
      editedItem: {
        dataAccessGroupId: "",
        homeVisitorDataAccessGroupId: "",
        name: "",
        emailAddress: "",
        secondaryEmailAddress: "",
        providerId: ""
      },
      defaultItem: {
        dataAccessGroupId: "",
        homeVisitorDataAccessGroupId: "",
        name: "",
        emailAddress: "",
        secondaryEmailAddress: "",
        providerId: ""
      },
      options: {
          format: 'YYYY-MM-DD',
          useCurrent: false,
      }    
    }),

    validations: {
      editedItem: {
        dataAccessGroupId: {
          required
        },
        homeVisitorDataAccessGroupId: {
          required
        },
        name: {
          required
        },
        emailAddress: {
          required
        },
        secondaryEmailAddress: {
          length: maxLength(254),
        },
        providerId: {
          required
        }
      }
    },

    computed: {
      formTitle () {
        return this.editedIndex === -1 ? 'New REDCap Submitter' : 'Edit REDCap Submitter'
      }
    },

    watch: {
      dialog (val) {
        val || this.close()
      }
    },

    created () {
      this.initialize()
    },

    methods: {
      fetchSubmitters() {
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/admin/redcap/submitters`)
            .then(response => {
                this.submitters = response.data
                this.isBusy = false
              console.log(response.data)
            })
      },

      fetchProviders() {
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/admin/providers`)
            .then(response => {
                this.providers = response.data
            })
      },

      initialize () {
          this.isBusy = true
          this.fetchSubmitters()
          this.fetchProviders()
      },

      editItem (item) {
        this.editedIndex = this.submitters.indexOf(item)
        this.editedItem = Object.assign({}, item)
        this.$bvModal.show('modal-crud')
      },

      deleteItem (item) {
        axios
          .delete(`${process.env.VUE_APP_API_BASE_URL}/admin/redcap/submitters/${item.dataAccessGroupId}`)
            .then(response => { 
                console.log(response)
                this.$bvToast.toast('REDCap Submitter Removed.', {
                  title: 'Status',
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: 'success',
                  appendToast: true
                })
                this.fetchSubmitters()   
            });
      },

      viewErrors (item) {
        this.$router.push({ name: 'redcapevents', params: { id: item.dataAccessGroupId} })
      },

      viewVisitorErrors (item) {
        this.$router.push({ name: 'redcapevents', params: { id: item.homeVisitorDataAccessGroupId } })
      },

      viewClosureErrors (item) {
        this.$router.push({ name: 'redcapClosureEvents', params: { id: item.dataAccessGroupId } })
      },

      close () {
        this.$bvModal.hide('modal-crud')
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
          this.fetchSubmitters()
        }, 300)
      },

      save () {
        if (this.editedIndex > -1) {
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/admin/redcap/submitters`, this.editedItem)
            .then(response => { 
                 this.$bvToast.toast('REDCap Submitter Saved.', {
                    title: 'Status',
                    toaster: 'b-toaster-top-center',
                    solid: true,
                    variant: 'success',
                    appendToast: true
                  })
            });
          
        } else {
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/admin/redcap/submitters`, this.editedItem)
            .then(response => { 
                this.$bvToast.toast('REDCap Submitter Saved.', {
                    title: 'Status',
                    toaster: 'b-toaster-top-center',
                    solid: true,
                    variant: 'success',
                    appendToast: true
                  })
            });
          
        }
        this.close()
      },

      formatDate (date) {
        if (!date) return null

        const [year, month, day] = date.split('-')
        return `${month}/${day}/${year}`
      },

      parseDate (date) {
        if (!date) return null

        const [month, day, year] = date.split('/')
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
    }
  }
</script>